import { formatHuman, parse } from '@edtf-ts/core'

const MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24

export const addDaysToDate = (date: Date, days: number) => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + days)
  return newDate
}

export const addMonthsToDate = (date: Date, months: number) => {
  const newDate = new Date(date)
  newDate.setMonth(newDate.getMonth() + months)
  return newDate
}

export const addYearsToDate = (date: Date, years: number) => {
  const newDate = new Date(date)
  newDate.setFullYear(newDate.getFullYear() + years)
  return newDate
}

const CURRENT_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
})

const DIFFERENT_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

export const formatDate = (date: Date) =>
  date.getFullYear() === new Date().getFullYear() ? CURRENT_YEAR_FORMATTER.format(date) : DIFFERENT_YEAR_FORMATTER.format(date)

// "Apr 24, 2026" — always year-qualified, unlike formatDate which omits the year in the current year
export const formatShortDateWithYear = (date: Date) => DIFFERENT_YEAR_FORMATTER.format(date)

// Human-friendly date + time: omits the year when it falls in the current year. Pass { utc: true } to
// format against UTC (e.g. scheduler nominal times) without showing a timezone label.
export const formatFriendlyDateTime = (date: Date, options?: { utc?: boolean }): string => {
  const utc = options?.utc ?? false
  const now = new Date()
  const sameYear = utc ? date.getUTCFullYear() === now.getUTCFullYear() : date.getFullYear() === now.getFullYear()
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(utc ? { timeZone: 'UTC' } : {}),
  })
}

// Formats a Date as a `YYYY-MM-DD` string using its local calendar day, so a date picked at
// local midnight is not shifted to the previous/next day the way `toISOString()` (UTC) would.
export const toLocalISODate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export const formatExpressiveDate = (date: Date) => {
  const now = new Date()

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / MILLISECONDS_IN_A_DAY)

  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const time = `${hours}:${minutes}`

  if (diffDays === 0) {
    return `Today, ${time}`
  }
  if (diffDays === 1) {
    return `Yesterday, ${time}`
  }

  const day = date.getDate()
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = monthNames[date.getMonth()]
  return `${day} ${month}, ${time}`
}

const RELATIVE_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const RELATIVE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
})

export const formatRelativeDateTime = (date: Date) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / MILLISECONDS_IN_A_DAY)

  const time = RELATIVE_TIME_FORMATTER.format(date)

  if (diffDays === 0) {
    return `today at ${time}`
  }
  if (diffDays === 1) {
    return `yesterday at ${time}`
  }

  return `${RELATIVE_DATE_FORMATTER.format(date)} at ${time}`
}

export const parseLocalISODate = (value?: string | null): Date | null => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '')
  if (!parts) {
    return null
  }
  const [, year, month, day] = parts
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  const roundTrips = date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)
  return roundTrips ? date : null
}

export const formatDateYYYYMMDD = (dateStr: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }

  const date = new Date(dateStr)
  if (isNaN(date.getTime())) {
    return ''
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const formatEdtfString = (edtfString: string) => {
  try {
    const parsed = parse(edtfString)
    if (!parsed.success) {
      return edtfString
    }
    return formatHuman(parsed.value)
  } catch (e) {
    console.error(e)
  }
  return edtfString
}
